import type { ProductInfo } from "@expert/contentful";

import { useNotification } from "@expert/common-ui";
import { type EligibleProduct, useProductOfferStore } from "@expert/sales-tools";
import { Box, Button, Checkbox, Container, Flex, Input, Select, Stack, Text } from "@mantine/core";
import { JSX, useEffect, useState } from "react";
import { IMaskInput } from "react-imask";

import { useAnalytics } from "@/analytics";
import { usePartnerConfig } from "@/partner/usePartnerConfig";
import { createEnrollment } from "@/sales-tools";
import { SalesFooter } from "@/sales-tools/home-product/components/StepSpecific/SalesFooter";
import { EnrollmentStatus, SaleProgress } from "@/sales-tools/home-product/types";
import { useLogger, usePartner, useSessionId, useTypedMostRecentTask } from "@/sdk";

import { GreenQuote, UncheckedRadioIcon } from "../Icons";
import classes from "./InlineSaleTrackerStep.module.css";

interface InlineSaleTrackerStepProps {
    productInfo: ProductInfo;
    product: EligibleProduct;
    productInfoLink?: string;
}

export function InlineSaleTrackerStep({
    productInfo,
    product,
    productInfoLink,
}: InlineSaleTrackerStepProps): JSX.Element | null {
    const logger = useLogger({ module: "InlineSaleTrackerStep", supportTeam: "sales" });
    const { dispatcher } = useAnalytics();
    const sessionId = useSessionId();
    const { voiceTask } = useTypedMostRecentTask();
    const partner = usePartner();
    const [confirmationCodeValue, setConfirmationCodeValue] = useState("");
    const [checked, setChecked] = useState(false);
    const notification = useNotification();
    const [additionalSku, setAdditionalSku] = useState<string | null>(null);
    const { blocklistedConfirmationCodes } = usePartnerConfig();

    useEffect(() => {
        setConfirmationCodeValue("");
        setAdditionalSku(null);
    }, [product, productInfo]);

    const handleAdditionalSkuChange = (value: string | null) => {
        setAdditionalSku(value);
    };

    const attSkusRequiringAdditionalSku = ["ATT_AAL", "ATT_AAL_DATA", "ATT_AAL_VOICE", "ATT_AAL_WEARABLE"];
    const attPASkus = ["ATT_PA1_NOT_WITH_AAL", "ATT_PA4_NOT_WITH_AAL", "ATT_INTERNET_AIR", "ATT_MI_FIBER"];

    const isValidConfirmationCode = product.confirmationCodePattern
        ? new RegExp(product.confirmationCodePattern).test(confirmationCodeValue)
        : true;

    const onAccept = async () => {
        void dispatcher.dispatchAction("ButtonClick", "Enroll", {
            productSku: product.productSku,
            confirmationCode: confirmationCodeValue,
        });

        const enroll = {
            callId: voiceTask?.agentCallId ?? sessionId,
            sessionId,
            partner,
            productOfferId: product.id,
            confirmationCode: confirmationCodeValue,
            productSku: product.productSku,
        };

        const response = await createEnrollment(logger, enroll);

        if (response.enrollmentStatus === EnrollmentStatus.Success) {
            useProductOfferStore.getState().updateProduct(sessionId, product.id, {
                ...product,
                saleProgress: SaleProgress.Success,
            });
        } else {
            logger.warn("There was an issue processing the enrollment");
            notification.error("There was an error processing the enrollment");
        }
    };

    const isBlocklistedCode = blocklistedConfirmationCodes?.includes(confirmationCodeValue) ?? false;

    const showAdditionalATTSku =
        attPASkus.concat(attSkusRequiringAdditionalSku).includes(product.productSku) && partner === "att";

    const isContinueDisabled =
        (productInfo.confirmationCheckboxText && !checked) ||
        (product.confirmationCodePattern && !isValidConfirmationCode) ||
        (attSkusRequiringAdditionalSku.includes(product.productSku) && !additionalSku) ||
        isBlocklistedCode;

    return (
        <Flex direction={"column"} justify={"space-between"} w="100%">
            <Stack p={14} gap={4} w="100%">
                <Flex direction="column" gap="xs">
                    <Text size="xs" c="dark.2">
                        Track the sale
                    </Text>
                </Flex>

                <form
                    onSubmit={(event) => {
                        event.preventDefault();
                    }}
                >
                    {showAdditionalATTSku ? (
                        <Stack className={classes.topPadding} gap={4}>
                            {!attPASkus.includes(product.productSku) && (
                                <>
                                    <Text py={8} px={4} fz="sm">
                                        {" "}
                                        Did you sell anything else?{" "}
                                    </Text>
                                    <Select
                                        value={additionalSku}
                                        classNames={{
                                            input: classes.styledInput,
                                            label: classes.descriptionText,
                                            dropdown: classes.optionsRoot,
                                        }}
                                        onChange={handleAdditionalSkuChange}
                                        size="sm"
                                        comboboxProps={{ zIndex: "var(--mantine-priority-highest)" }}
                                        w={247}
                                        placeholder="Choose an option"
                                        data={[
                                            { value: "NO", label: "No" },
                                            { value: "ATT_PA1_STATIC", label: "Yes, Protect Advantage 1" },
                                            { value: "ATT_PA4_STATIC", label: "Yes, Protect Advantage 4" },
                                        ]}
                                    />
                                </>
                            )}

                            <Text fz="sm" py={8} px={4}>
                                Complete the sale in Opus and add the confirmation code:
                            </Text>

                            <Input
                                value={confirmationCodeValue}
                                onChange={(event) => {
                                    setConfirmationCodeValue(event.currentTarget.value);
                                }}
                                component={IMaskInput}
                                placeholder="Confirmation code"
                                size="sm"
                                mask="00-000000000000000"
                                w={247}
                                classNames={{ input: classes.styledInput }}
                            />
                        </Stack>
                    ) : partner === "youtubetv" ? (
                        <Flex p="16px 4px">
                            <Container p={0}>
                                <GreenQuote />
                            </Container>
                            <Text c="dark.1" size="sm" pl="sm">
                                {productInfo?.confirmationCodeTitle}
                            </Text>
                        </Flex>
                    ) : (
                        product?.confirmationCodePattern && (
                            <Box className={classes.inputWrapper} py={8}>
                                <Flex>
                                    <Box flex={0}>
                                        <UncheckedRadioIcon />
                                    </Box>
                                    <Text fz={14} pb={12} pl={2}>
                                        {productInfo?.confirmationCodeTitle ?? "Enter the order number"}
                                    </Text>
                                </Flex>

                                <Input
                                    value={confirmationCodeValue}
                                    onChange={(event) => {
                                        setConfirmationCodeValue(event.currentTarget.value);
                                    }}
                                    component={IMaskInput}
                                    placeholder={productInfo?.confirmationCodePlaceholder ?? "Order number"}
                                    size="32px"
                                    mask={/^[a-zA-Z0-9]*$/}
                                    classNames={{ input: classes.styledInput }}
                                    w={250}
                                    pl={"md"}
                                />
                            </Box>
                        )
                    )}

                    {productInfo.confirmationCheckboxText && (
                        <Checkbox
                            c="dark.1"
                            label={productInfo.confirmationCheckboxText}
                            checked={checked}
                            onChange={(event) => setChecked(event.currentTarget.checked)}
                        />
                    )}

                    {productInfo.subtext && (
                        <Text c="dark.2" size="sm" mt="xl">
                            {productInfo.subtext}
                        </Text>
                    )}

                    {productInfoLink && (
                        <Button
                            onClick={() => window.open(productInfoLink, "_blank")?.focus()}
                            classNames={{ root: classes.linkButton }}
                            size="xxs"
                            p={0}
                            variant="transparent"
                        >
                            <Text size="xs" td="underline">
                                View product page
                            </Text>
                        </Button>
                    )}
                </form>
            </Stack>

            <SalesFooter
                continueButtonProps={{
                    text: "I enrolled the customer",
                    onClick: onAccept,
                    isDisabled: isContinueDisabled,
                }}
                product={product}
            />
        </Flex>
    );
}
